/**
 * WidgetSpanHandle - the widget's right edge, dragged to change its width.
 *
 * The pane surface has `PaneSplitter` for the vertical axis; this is the same
 * gesture turned ninety degrees and quantized, because a widget's width is not
 * continuous: it is 1, 2, 3 or 4 QUARTERS of a row (`SPAN_UNITS`). Everything
 * about how the drag is run is copied from the splitter on purpose:
 *
 *   • THE WINDOW-LEVEL NET. The pointer leaves this 8px strip immediately, so
 *     listening on the element would start a drag that never ends. Move and
 *     release are bound to the window for the life of the gesture and removed
 *     after, including on pointercancel and Escape.
 *   • FRACTIONS, NOT PIXELS. The delta is divided by the ROW's measured width
 *     before it reaches the model, so a quarter means a quarter on any window.
 *   • IT STOPS PROPAGATION. The chrome bar above is the reorder drag handle,
 *     and a press on the resize edge must never be mistaken for one.
 *
 * The one thing it adds: the drag is measured from where it STARTED, not
 * frame to frame. A quantized handle that accumulated deltas would round each
 * frame's fraction to zero and never move.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { SPAN_UNITS, WidgetSpan } from './widgetLayout';

interface Props {
  /** The row this widget sits in - measured to convert px → span units. */
  rowRef: React.RefObject<HTMLElement>;
  /** Where the widget starts, so a drag can be read as an absolute width. */
  span: WidgetSpan;
  /** Narrowest this widget may go. */
  minSpan: WidgetSpan;
  /** Already-translated, for the separator's accessible name. */
  label: string;
  onResize: (span: number) => void;
  disabled?: boolean;
}

const clamp = (span: number, min: number) =>
  Math.min(SPAN_UNITS, Math.max(min, Math.round(span)));

const WidgetSpanHandle: React.FC<Props> = ({
  rowRef,
  span,
  minSpan,
  label,
  onResize,
  disabled,
}) => {
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; span: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled || (typeof e.button === 'number' && e.button > 0)) return;
    // The chrome bar above is the reorder handle; this press is not that.
    e.stopPropagation();
    e.preventDefault();
    start.current = { x: e.clientX, span };
    setDragging(true);
  }, [disabled, span]);

  useEffect(() => {
    if (!dragging) return undefined;

    const move = (e: PointerEvent) => {
      const width = rowRef.current?.getBoundingClientRect().width || 0;
      if (!width || !start.current) return;
      const units = ((e.clientX - start.current.x) / width) * SPAN_UNITS;
      const next = clamp(start.current.span + units, minSpan);
      if (next !== span) onResize(next);
    };
    const stop = () => { start.current = null; setDragging(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') stop(); };

    const previousSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    window.addEventListener('keydown', key);
    return () => {
      document.body.style.userSelect = previousSelect;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      window.removeEventListener('keydown', key);
    };
  }, [dragging, rowRef, minSpan, span, onResize]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); onResize(clamp(span - 1, minSpan)); }
    if (e.key === 'ArrowRight') { e.preventDefault(); onResize(clamp(span + 1, minSpan)); }
  }, [disabled, onResize, span, minSpan]);

  if (disabled) return null;

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={span}
      aria-valuemin={minSpan}
      aria-valuemax={SPAN_UNITS}
      tabIndex={0}
      data-testid="widget-span-handle"
      data-widget-span-handle={span}
      data-dragging={dragging}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={clsx(
        'absolute top-0 end-0 h-full w-2 -me-1 z-10',
        'flex items-center justify-center cursor-col-resize group/span focus:outline-none'
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'w-0.5 h-10 rounded-full transition-colors duration-200 ease-slide',
          dragging
            ? 'bg-champagne'
            : 'bg-champagne/30 group-hover/span:bg-champagne/70 group-focus/span:bg-champagne/70'
        )}
      />
    </div>
  );
};

export default WidgetSpanHandle;
